import React, { useState } from "react"
import { Button, Input, List, Space, message } from "antd"
import type { OrderCommentInterface, OrderCommentType } from "../../types"
import { SendOutlined } from "@ant-design/icons"
import { useTheme } from "styled-components"

interface OrderCommentsProps {
    comments: OrderCommentInterface[]
    admin?: boolean
    onAddComment?: ({ content, type }: { content: string; type: OrderCommentType }) => Promise<void>
}

export const OrderComments: React.FC<OrderCommentsProps> = ({ comments, admin, onAddComment }) => {
    const [newComment, setNewComment] = useState("")
    const [submitting, setSubmitting] = useState(false)
    const theme=useTheme()
    const handleAddComment = async (type: string) => {
        if (!newComment.trim()) return
        setSubmitting(true)
        try {
            if (onAddComment) {
                await onAddComment({ content: newComment.trim(), type: type as OrderCommentType })
                setNewComment("")
            }
        } catch (e) {
            message.error("Failed to add comment")
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Space direction="vertical" style={{ width: "100%" }}>
            {comments && comments.length > 0 && (
                <List
                    size="small"
                    dataSource={comments}
                    renderItem={(item) => (
                        <List.Item key={item.id}>
                            <div style={{ lineHeight: 1.5 }}>
                                <span style={{ fontWeight: 500, color: "#1677ff" }}>{item.user?.firstName}</span>
                                <span style={{ color: "#888", marginLeft: 8, fontSize: "0.95em" }}>
                                    {new Date(item.ts).toLocaleString()}
                                </span>
                                <div
                                    style={{
                                        marginTop: 2,
                                        background:
                                            item.type === "processing"
                                                ? "#fffbe6"
                                                : item.type === "system"
                                                ? "#e6f7ff"
                                                : undefined,
                                        borderLeft:
                                            item.type === "processing"
                                                ? "4px solid #faad14"
                                                : item.type === "system"
                                                ? "4px solid #1890ff"
                                                : undefined,
                                        paddingLeft:
                                            item.type === "processing" || item.type === "system" ? 8 : undefined,
                                    }}
                                >
                                    {item.content}
                                    {(item.type === "processing" || item.type === "system") && (
                                        <span
                                            style={{
                                                marginLeft: 8,
                                                color: "#faad14",
                                                fontWeight: 500,
                                                fontSize: "0.9em",
                                            }}
                                        >
                                            [{item.type}]
                                        </span>
                                    )}
                                </div>
                            </div>
                        </List.Item>
                    )}
                    locale={{ emptyText: "No comments yet" }}
                />
            )}
            <Input.TextArea
                rows={2}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                maxLength={500}
            />
            <Button
                type="primary"
                icon={<SendOutlined />}
                style={{
                    background: theme.secondary,
                    borderColor: "#1677ff",
                    borderRadius: 20,
                    fontWeight: 500,
                    padding: "0 20px",
                    color:theme.white,
                    boxShadow: "0 2px 8px rgba(22,119,255,0.08)",
                    marginTop: 8,
                }}
                onClick={() => handleAddComment(admin ? "response" : "user")}
                loading={submitting}
                disabled={!newComment.trim()}
            >
                Send
            </Button>
            {admin && (
                <div>
                    <Button
                        onClick={() => handleAddComment("processing")}
                        loading={submitting}
                        disabled={!newComment.trim()}
                    >
                        Add Processing Notes
                    </Button>
                    <span style={{ color: "#888", fontSize: "0.95em", marginLeft: 8 }}>(Only Admin can see processing notes)</span>
                </div>
            )}
        </Space>
    )
}
